import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Sprout, Droplets, FileSignature } from "lucide-react";

const ClientTurfCard = ({ project, onChange }) => {
  const [detail, setDetail] = useState(null);
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/turf/${project.id}`).then((r) => setDetail(r.data));
  useEffect(() => { load(); }, [project.id]);

  const sign = async () => {
    if (!name || !agree) { toast.error("Type your name and accept the terms"); return; }
    setBusy(true);
    try {
      await api.post(`/turf/${project.id}/sign-waiver`, { signature_name: name, agree });
      toast.success("Waiver signed · renovation launched");
      await load(); onChange?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!detail) return null;
  const draft = detail.status === "draft";

  return (
    <div data-testid={`turf-client-${project.id}`} className="rounded-2xl border border-black/5 bg-white p-6 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="font-semibold text-emerald-950">{detail.address}</p>
          <p className="text-xs text-stone-500">{detail.infestation_type.join(", ")} · {detail.square_feet} sq ft · {detail.sod_type}</p>
        </div>
        <Badge className={`rounded-full ${draft ? "bg-red-100 text-red-700" : detail.current_phase === "Completed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{draft ? "Waiver required" : detail.current_phase}</Badge>
      </div>

      {draft ? (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50/50 p-4">
          <p className="text-xs uppercase tracking-[0.2em] text-red-700 flex items-center gap-2"><FileSignature className="w-4 h-4" /> Biological liability waiver</p>
          <pre data-testid={`waiver-text-${project.id}`} className="mt-2 max-h-44 overflow-auto whitespace-pre-wrap text-[11px] leading-relaxed text-stone-600">{detail.waiver_text}</pre>
          <label className="mt-3 flex items-start gap-2 text-sm text-stone-700 cursor-pointer">
            <Checkbox data-testid={`waiver-agree-${project.id}`} checked={agree} onCheckedChange={(v) => setAgree(!!v)} />
            <span>I read, understand, and agree to the biological terms of this turf renovation contract.</span>
          </label>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Input data-testid={`waiver-name-${project.id}`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Type your full name to sign" className="rounded-xl max-w-xs" />
            <Button data-testid={`waiver-sign-${project.id}`} onClick={sign} disabled={busy} className="rounded-full bg-emerald-900 hover:bg-emerald-800 text-white">Approve renovation job</Button>
          </div>
        </div>
      ) : (
        detail.sod_irrigation?.length > 0 && (
          <div data-testid={`turf-irrigation-${project.id}`} className="mt-4">
            <p className="text-xs uppercase tracking-[0.2em] text-emerald-700 flex items-center gap-2"><Droplets className="w-4 h-4" /> New-sod smart irrigation</p>
            <div className="mt-3 space-y-2">
              {detail.sod_irrigation.map((s, i) => (
                <div key={i} className={`rounded-xl border p-3 ${s.active ? "border-emerald-300 bg-emerald-50/60" : "border-stone-100 bg-stone-50/60"}`}>
                  <div className="flex items-center justify-between flex-wrap gap-1">
                    <span className="text-sm font-semibold text-emerald-950">{s.phase}{s.active ? " · active" : ""}</span>
                    <span className="font-mono-ag text-xs text-amber-700">{s.schedule}</span>
                  </div>
                  <p className="text-xs text-stone-600 mt-1">{s.details}</p>
                </div>
              ))}
            </div>
          </div>
        )
      )}
    </div>
  );
};

export const TurfProjects = () => {
  const [projects, setProjects] = useState([]);
  const load = () => api.get("/turf").then((r) => setProjects(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (projects.length === 0) return null;
  return (
    <div data-testid="turf-projects" className="mt-8">
      <h2 className="font-serif text-2xl text-emerald-950 flex items-center gap-2"><Sprout className="w-5 h-5 text-emerald-700" /> Turf Renovation</h2>
      <p className="text-xs text-stone-400 mt-1">Your lawn eradication & re-sod projects managed by your landcare crew.</p>
      <div className="mt-4 grid lg:grid-cols-2 gap-4">
        {projects.map((p) => <ClientTurfCard key={p.id} project={p} onChange={load} />)}
      </div>
    </div>
  );
};
